import {
  IconCameraBolt,
  IconHeart,
  IconPencilPlus,
  IconPhoto,
  IconSparkles,
  IconSwipe,
  IconUserHeart,
  IconX,
} from '@tabler/icons-react-native';
import { Button, type ButtonProps } from 'app/design/primitives/button';
import { Gradient } from 'app/design/primitives/gradient';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { useTheme } from 'app/shopify';
import type { SuggestedListing } from 'app/network/suggested-listing';
import { useEffect, useState, type ReactNode } from 'react';
import { Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useTranslation } from 'react-i18next';
import tutorialPointer from './assets/tutorial-pointer.svg';
import { RevolvingCardCarousel } from './revolving-card-carousel';
import { getTutorialCardLayout } from './tutorial-layout';
import { hubColors, styles } from './styles';
const tutorialStyles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFill,
    zIndex: 20,
    elevation: 20,
    backgroundColor: 'rgba(40, 39, 37, 0.36)',
  },
  highlightedTabs: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    position: 'absolute',
    gap: 15,
    padding: 16,
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 9 },
    shadowOpacity: 0.25,
    shadowRadius: 8.5,
    elevation: 10,
  },
  pointer: { position: 'absolute', top: -8, width: 24.4, height: 16.3 },
  copy: { gap: 8 },
  tutorialTitle: { paddingRight: 30, color: '#1F2635', fontSize: 16, lineHeight: 19 },
  close: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 30,
    height: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  description: { paddingRight: 4, color: '#1F2635', fontSize: 14, lineHeight: 17 },
  footer: { minHeight: 36, flexDirection: 'row', alignItems: 'center', gap: 15 },
  stepLabel: { flex: 1, color: '#595B5E', fontSize: 16, lineHeight: 19 },
  actions: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  tutorialButton: {
    minHeight: 36,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 1000,
  },
  tutorialButtonOutline: { borderWidth: 1, borderColor: '#1F2635', backgroundColor: '#FFFFFF' },
  tutorialButtonFilled: { backgroundColor: '#1F2635' },
  tutorialButtonPressed: { opacity: 0.72 },
  tutorialButtonText: { color: '#1F2635', fontSize: 14, lineHeight: 16 },
  tutorialButtonTextFilled: { color: '#FFFFFF' },
});
export { hubColors, styles } from './styles';

export type HubTab = 'suggestions' | 'photos' | 'manual';
const tabs = [
  { id: 'suggestions', Icon: IconSwipe },
  { id: 'photos', Icon: IconCameraBolt },
  { id: 'manual', Icon: IconPencilPlus },
] as const;

export function HubTabs({
  selected,
  onSelect,
  canUseAI,
  onWidthChange,
  onTabLayout,
}: {
  selected: HubTab;
  onSelect: (tab: HubTab) => void;
  canUseAI: boolean;
  onWidthChange?: (width: number) => void;
  onTabLayout?: (tab: HubTab, x: number, width: number) => void;
}) {
  const theme = useTheme();
  const { width } = useWindowDimensions();
  const { t } = useTranslation('offer-hub');
  return (
    <View
      accessibilityRole="tablist"
      style={[styles.tabs, { backgroundColor: theme.colors['surface.primary-neutral'] }]}
      onLayout={(event) => onWidthChange?.(event.nativeEvent.layout.width)}
    >
      {tabs
        .filter(({ id }) => id !== 'photos' || canUseAI)
        .map(({ id, Icon }) => (
          <HubTabButton
            key={id}
            selected={id === selected}
            label={t(`tabs.${id}`)}
            Icon={Icon}
            maxLabelWidth={Math.max(44, Math.min(width, 480) - 264)}
            onPress={() => onSelect(id)}
            onLayout={(x, tabWidth) => onTabLayout?.(id, x, tabWidth)}
          />
        ))}
    </View>
  );
}

export type HubTutorialStep = 0 | 1 | 2;

const allTutorialTabs: HubTab[] = ['suggestions', 'photos', 'manual'];
const tutorialTabsWithoutAI: HubTab[] = ['suggestions', 'manual'];
interface TabMeasurement {
  x: number;
  width: number;
}

export function HubTutorial({
  step,
  topInset,
  bottomInset,
  canUseAI,
  onStepChange,
  onDismiss,
}: {
  step: HubTutorialStep;
  topInset: number;
  bottomInset: number;
  canUseAI: boolean;
  onStepChange: (step: HubTutorialStep) => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation('offer-hub');
  const { width: windowWidth } = useWindowDimensions();
  const [overlayWidth, setOverlayWidth] = useState(0);
  const [tabStripWidth, setTabStripWidth] = useState(0);
  const [tabMeasurements, setTabMeasurements] = useState<Partial<Record<HubTab, TabMeasurement>>>({});
  const tutorialTabs = canUseAI ? allTutorialTabs : tutorialTabsWithoutAI;
  const availableWidth = overlayWidth || windowWidth;
  const { cardLeft, cardWidth } = getTutorialCardLayout(availableWidth, step, canUseAI);
  const currentTab = tutorialTabs[step] ?? tutorialTabs.at(-1) ?? 'suggestions';
  const selectedTabMeasurement = tabMeasurements[currentTab];
  const fallbackTargetOffsets: readonly number[] = canUseAI ? [-48, 0, 48] : [-24, 24];
  const fallbackTargetCenter = availableWidth / 2 + (fallbackTargetOffsets[step] ?? fallbackTargetOffsets.at(-1) ?? 0);
  const targetCenter =
    tabStripWidth > 0 && selectedTabMeasurement
      ? availableWidth / 2 - tabStripWidth / 2 + selectedTabMeasurement.x + selectedTabMeasurement.width / 2
      : fallbackTargetCenter;
  const pointerLeft = Math.max(16, Math.min(targetCenter - cardLeft - 12.2, cardWidth - 40));
  const cardTop = topInset + (step === 0 ? 57 : 60);
  const lastStep = step === tutorialTabs.length - 1;
  const handleTabStripWidthChange = (nextWidth: number) => {
    setTabStripWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
  };
  const handleOverlayLayout = (nextWidth: number) => {
    setOverlayWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
  };
  const handleTabLayout = (tab: HubTab, x: number, tabWidth: number) => {
    setTabMeasurements((currentMeasurements) => {
      const current = currentMeasurements[tab];
      if (current?.x === x && current.width === tabWidth) return currentMeasurements;
      return { ...currentMeasurements, [tab]: { x, width: tabWidth } };
    });
  };
  const goBack = () => {
    if (step === 0) onDismiss();
    else onStepChange((step - 1) as HubTutorialStep);
  };
  const goForward = () => {
    if (lastStep) onDismiss();
    else onStepChange((step + 1) as HubTutorialStep);
  };

  return (
    <View
      testID="offer-hub-tutorial"
      style={[tutorialStyles.overlay, { bottom: bottomInset }]}
      onLayout={(event) => handleOverlayLayout(event.nativeEvent.layout.width)}
      accessibilityViewIsModal
      onAccessibilityEscape={onDismiss}
    >
      <View
        testID="offer-hub-tutorial-tabs"
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        style={[tutorialStyles.highlightedTabs, { top: topInset + 4 }]}
      >
        <HubTabs
          selected={currentTab}
          onSelect={() => undefined}
          canUseAI={canUseAI}
          onWidthChange={handleTabStripWidthChange}
          onTabLayout={handleTabLayout}
        />
      </View>
      <View
        testID="offer-hub-tutorial-card"
        accessibilityLiveRegion="polite"
        style={[
          tutorialStyles.card,
          {
            left: cardLeft,
            top: cardTop,
            width: cardWidth,
          },
        ]}
      >
        <PCImage
          source={tutorialPointer}
          contentFit="fill"
          style={[tutorialStyles.pointer, { left: pointerLeft }]}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        />
        <View style={tutorialStyles.copy}>
          <Text style={tutorialStyles.tutorialTitle} fontWeight="700">
            {t(`tutorial.steps.${currentTab}.title`)}
          </Text>
          <Pressable
            testID="offer-hub-tutorial-close"
            accessibilityRole="button"
            accessibilityLabel={t('tutorial.close')}
            hitSlop={8}
            style={tutorialStyles.close}
            onPress={onDismiss}
          >
            <IconX size={24} color="#1F2635" />
          </Pressable>
          <Text style={tutorialStyles.description}>{t(`tutorial.steps.${currentTab}.description`)}</Text>
        </View>
        <View style={tutorialStyles.footer}>
          <Text style={tutorialStyles.stepLabel} color="text.secondary">
            {t('tutorial.step', { current: step + 1, total: tutorialTabs.length })}
          </Text>
          <View style={tutorialStyles.actions}>
            <TutorialButton testID="offer-hub-tutorial-back" label={t('tutorial.back')} onPress={goBack} />
            <TutorialButton
              testID="offer-hub-tutorial-next"
              label={t(lastStep ? 'tutorial.finish' : 'tutorial.next')}
              filled
              onPress={goForward}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

function TutorialButton({
  testID,
  label,
  filled = false,
  onPress,
}: {
  testID: string;
  label: string;
  filled?: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      style={({ pressed }) => [
        tutorialStyles.tutorialButton,
        filled ? tutorialStyles.tutorialButtonFilled : tutorialStyles.tutorialButtonOutline,
        pressed && tutorialStyles.tutorialButtonPressed,
      ]}
      onPress={onPress}
    >
      <Text
        style={[tutorialStyles.tutorialButtonText, filled && tutorialStyles.tutorialButtonTextFilled]}
        fontWeight="500"
      >
        {label}
      </Text>
    </Pressable>
  );
}

function HubTabButton({
  selected,
  label,
  Icon,
  onPress,
  onLayout,
  maxLabelWidth,
}: {
  selected: boolean;
  label: string;
  Icon: typeof IconSwipe;
  onPress: () => void;
  onLayout?: (x: number, width: number) => void;
  maxLabelWidth: number;
}) {
  const reducedMotion = useReducedMotion();
  const progress = useSharedValue(selected ? 1 : 0);
  const [labelWidth, setLabelWidth] = useState(label.length * 9);
  const expandedLabelWidth = Math.min(Math.max(labelWidth, label.length * 9) + 16, maxLabelWidth);

  useEffect(() => {
    progress.value = withTiming(selected ? 1 : 0, { duration: reducedMotion ? 0 : 240 });
  }, [progress, reducedMotion, selected]);

  const tabStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(progress.value, [0, 1], ['#F8F7F5', hubColors.ink]),
  }));
  const labelStyle = useAnimatedStyle(() => ({
    width: progress.value * expandedLabelWidth,
    opacity: progress.value,
  }));

  return (
    <Pressable
      accessibilityRole="tab"
      accessibilityLabel={label}
      accessibilityState={{ selected }}
      onPress={onPress}
      onLayout={(event) => onLayout?.(event.nativeEvent.layout.x, event.nativeEvent.layout.width)}
    >
      <Animated.View style={[styles.tab, tabStyle]}>
        <Icon size={24} color={selected ? '#FFFFFF' : hubColors.ink} strokeWidth={2} />
        <Animated.View aria-hidden={!selected} style={[styles.tabLabelClip, labelStyle]}>
          <Text numberOfLines={1} style={[styles.body, styles.tabLabel, { color: '#FFFFFF' }]} fontWeight="600">
            {label}
          </Text>
        </Animated.View>
        <View
          pointerEvents="none"
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          aria-hidden
          style={styles.measureLabel}
        >
          <Text
            onLayout={(event) => setLabelWidth(event.nativeEvent.layout.width)}
            style={styles.body}
            fontWeight="600"
          >
            {label}
          </Text>
        </View>
      </Animated.View>
    </Pressable>
  );
}

export function HubButton({
  children,
  tone = 'ink',
  size = 'large',
  prefix,
  suffix,
  style,
  disabled,
  ...props
}: ButtonProps & { tone?: 'ink' | 'teal' | 'outline' | 'ghost'; size?: 'large' | 'small' }) {
  const inverse = tone === 'ink' || tone === 'teal';
  return (
    <Button
      accessibilityRole="button"
      {...props}
      disabled={disabled}
      variant="plain"
      style={[
        styles.button,
        size === 'small' && styles.smallButton,
        {
          ...(tone === 'ink'
            ? { backgroundColor: hubColors.ink }
            : tone === 'teal'
              ? { backgroundColor: hubColors.teal }
              : {}),
          borderColor: tone === 'outline' ? hubColors.ink : 'transparent',
          borderWidth: tone === 'outline' ? 1.5 : 0,
          opacity: disabled ? 0.4 : 1,
        },
        style,
      ]}
    >
      {prefix}
      <Text
        style={[
          styles.body,
          size === 'small' && styles.smallButtonText,
          { color: inverse ? '#FFFFFF' : hubColors.ink, flexShrink: 1 },
        ]}
        fontWeight="600"
        textAlign="center"
      >
        {children}
      </Text>
      {suffix}
    </Button>
  );
}

export function HubProgress({
  remaining,
  total,
  inverse = false,
}: {
  remaining: number;
  total: number;
  inverse?: boolean;
}) {
  const theme = useTheme();
  const { t } = useTranslation('offer-hub');
  return (
    <View style={styles.progress}>
      <Text style={{ fontSize: 18, lineHeight: 22, color: inverse ? '#FFFFFF' : hubColors.ink }} fontWeight="600">
        {t('listRemaining', { count: remaining })}
      </Text>
      <View
        accessibilityRole="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={total - remaining}
        accessibilityLabel={t('roundProgress')}
        accessibilityValue={{ min: 0, max: total, now: total - remaining }}
        style={[styles.track, { backgroundColor: inverse ? '#20273733' : theme.colors['surface.tertiary-neutral'] }]}
      >
        <View
          style={{
            height: 4,
            borderRadius: 2,
            width: `${String(Math.max(6, ((total - remaining) / Math.max(total, 1)) * 100))}%` as `${number}%`,
            backgroundColor: inverse ? hubColors.mint : hubColors.teal,
          }}
        />
      </View>
    </View>
  );
}

export function HubCard({
  children,
  image,
  stacked = false,
  stackImage,
}: {
  children: ReactNode;
  image?: string;
  stacked?: boolean;
  stackImage?: string;
}) {
  const theme = useTheme();
  return (
    <View style={styles.flex}>
      {stacked ? (
        <View
          style={[
            styles.stack,
            styles.card,
            { backgroundColor: theme.colors['surface.primary-neutral'], borderColor: theme.colors['border.secondary'] },
          ]}
        >
          {stackImage ? (
            <PCImage
              source={{ uri: stackImage }}
              contentFit="cover"
              style={[StyleSheet.absoluteFill, styles.clipped]}
            />
          ) : null}
        </View>
      ) : null}
      <View
        style={[
          styles.card,
          styles.flex,
          { backgroundColor: theme.colors['surface.primary-neutral'], borderColor: theme.colors['border.secondary'] },
        ]}
      >
        <View style={[StyleSheet.absoluteFill, styles.clipped]}>
          {image ? (
            <>
              <PCImage
                key={image}
                source={{ uri: image }}
                blurRadius={5}
                contentFit="cover"
                style={StyleSheet.absoluteFill}
              />
              <View style={[StyleSheet.absoluteFill, { backgroundColor: '#00000066' }]} />
              <View style={[StyleSheet.absoluteFill, { backgroundColor: '#FFFFFF3D' }]} />
            </>
          ) : null}
        </View>
        <View style={styles.center}>{children}</View>
      </View>
    </View>
  );
}

export function HubCopy({
  title,
  description,
  inverse = false,
}: {
  title: string;
  description?: string;
  inverse?: boolean;
}) {
  const theme = useTheme();
  return (
    <View style={styles.copy}>
      <Text
        accessibilityRole="header"
        style={[styles.title, { color: inverse ? '#FFFFFF' : hubColors.ink }]}
        fontWeight="600"
        textAlign="center"
      >
        {title}
      </Text>
      {description ? (
        <Text style={[styles.body, { color: inverse ? '#FFFFFF' : theme.colors['text.secondary'] }]} textAlign="center">
          {description}
        </Text>
      ) : null}
    </View>
  );
}
export function HubEmoji({ children }: { children: string }) {
  return <Text style={{ fontSize: 40, lineHeight: 48 }}>{children}</Text>;
}

export function ItemFace({ item, announce = false }: { item: SuggestedListing; announce?: boolean }) {
  const theme = useTheme();
  const [failed, setFailed] = useState(false);
  const picture = item.listingGroup.picture;
  const title = item.listingGroup.name ?? '';
  return (
    <View style={[styles.flex, styles.clipped, { backgroundColor: theme.colors['surface.secondary-neutral'] }]}>
      {picture && !failed ? (
        <PCImage
          source={{ uri: picture }}
          contentFit="contain"
          style={StyleSheet.absoluteFill}
          onError={() => setFailed(true)}
        />
      ) : (
        <View style={styles.center}>
          <IconPhoto size={56} color={theme.colors['icon.tertiary']} />
        </View>
      )}
      <Gradient
        from="#000000"
        fromOpacity={0}
        to="#000000"
        toOpacity={0.4}
        style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '60%' }}
      />
      <Text
        accessibilityLiveRegion={announce ? 'polite' : 'none'}
        numberOfLines={3}
        fontWeight="600"
        style={[
          styles.title,
          styles.itemName,
          {
            color: failed || !picture ? hubColors.ink : '#FFFFFF',
            textShadowColor: theme.colors['shadow.strong'],
            textShadowOffset: { width: 0, height: 1 },
            textShadowRadius: 2,
          },
        ]}
      >
        {title}
      </Text>
    </View>
  );
}

export function QuickOffersOverview({
  items,
  remaining,
  total,
  active,
}: {
  items: SuggestedListing[];
  remaining?: number;
  total?: number;
  active: boolean;
}) {
  const theme = useTheme();
  const { t } = useTranslation('offer-hub');
  const inProgress = remaining !== undefined && total !== undefined && remaining < total;
  const displayedCount = inProgress ? remaining : 10;

  return (
    <View style={styles.quickOverview}>
      <View style={styles.quickOfferCardShadow}>
        <View style={[styles.quickOfferCard, { backgroundColor: theme.colors['surface.secondary-neutral'] }]}>
          <View style={styles.quickSignal}>
            <Text style={styles.quickSignalText} fontWeight="600">
              {t('quickSignal', { count: displayedCount, seconds: displayedCount * 2 })}
            </Text>
          </View>
          <Text style={styles.quickOfferTitle} fontWeight="600" textAlign="center">
            {t('quickTitle')}
          </Text>
          <RevolvingCardCarousel items={items} accessibilityLabel={t('quickTitle')} active={active} />
        </View>
      </View>
      <View style={styles.quickBenefits}>
        <Text style={styles.title} fontWeight="600">
          {t('whyPublish')}
        </Text>
        <View style={styles.quickBenefitList}>
          {[
            {
              title: t('benefitReciprocity'),
              Icon: IconHeart,
            },
            {
              title: t('benefitTrust'),
              Icon: IconSparkles,
            },
            {
              title: t('benefitKarma'),
              Icon: IconUserHeart,
            },
          ].map(({ title, Icon }) => (
            <View key={title} style={styles.quickBenefitRow}>
              <Icon size={24} color={hubColors.ink} />
              <View style={styles.quickBenefitCopy}>
                <Text style={styles.quickBenefitText} fontWeight="600">
                  {title}
                </Text>
              </View>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}
